import * as React from 'react';
import { useEffect, useState } from 'react';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import CheckRegisterPeriodStat from 'src/models/personnel/CheckRegisterPeriodStat';
import CheckRegisterPeriodTypeStat from 'src/models/personnel/CheckRegisterPeriodTypeStat';
import CheckRegisterPeriodOfficeStat from 'src/models/personnel/CheckRegisterPeriodOfficeStat';
import CheckRegisterOfficeClientStat from 'src/models/personnel/CheckRegisterOfficeClientStat';
import CheckRegisterStatsDetailItem from 'src/models/personnel/CheckRegisterStatsDetailItem';
import { ODataOptions } from 'src/util/ODataBuilder';

interface ICheckRegisterStatsResult {
    byPeriod: Array<CheckRegisterPeriodStat>;
    byType: Array<CheckRegisterPeriodTypeStat>;
    byOffice: Array<CheckRegisterPeriodOfficeStat>;
    byClient: Array<CheckRegisterOfficeClientStat>;
    byCheck: Array<object>;
}

interface ICheckRegisterStatsDialogProps {
    dialogName: string;
    hideDialog: () => void;
}

const money = (value: number) => (value ?? 0).toFixed(2);
const sum = (values: Array<number | undefined>) => values.reduce((total: number, value) => total + (value ?? 0), 0);

// Small local copies of CheckRegisterPage.tsx's formatDateForDisplay/displayDisbursement -
// deliberately not imported from there, since this dialog is itself mounted by that page and
// importing back from it would set up a circular module dependency for two three-line helpers.
const formatDate = (value?: Date | string) => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) {
        return '';
    }
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
};
const disbursement = (checkType?: string) => (checkType === 'D' ? 'Direct Deposit' : checkType === 'C' ? 'Paper Check' : checkType ?? '');

// Applied to every header/data cell across all 5 stats tables - without these, plain unstyled
// th/td cells have no consistent padding or separator, so columns end up sized purely by
// content width with nothing visually tying a header to the data below it (looked "gappy" and
// misaligned).
const thStyle: React.CSSProperties = { padding: '.35rem .5rem', textAlign: 'left', borderBottom: '2px solid #999', whiteSpace: 'nowrap' };
const tdStyle: React.CSSProperties = { padding: '.35rem .5rem', borderBottom: '1px solid #ddd' };
const totalRowStyle: React.CSSProperties = { backgroundColor: '#8ee08e', fontWeight: 'bold' };

// Mirrors the Check_Register_Period/_Period_Type/_Period_Office/_Office_Client blocks (opened by
// the main screen's "Check Stats" button) - each backed by its own precomputed summary table,
// each with its own green Total row (confirmed via the real Form Builder layout screenshot).
// byCheck ("Check No.") is a master-detail relation off a selected "by Office" row (CHECK_REGISTER1
// in the .fmt queries CHECK_REGISTER directly, filtered to that row's period_no/type/office) -
// not a flat list alongside the other four, so it only populates once an Office row is clicked.
const CheckRegisterStatsDialog = (props: ICheckRegisterStatsDialogProps) => {
    const { get, response } = useFetch();
    const [stats, setStats] = useState<ICheckRegisterStatsResult>({ byPeriod: [], byType: [], byOffice: [], byClient: [], byCheck: [] });
    const [selectedOffice, setSelectedOffice] = useState<CheckRegisterPeriodOfficeStat | null>(null);
    const [checkDetail, setCheckDetail] = useState<Array<CheckRegisterStatsDetailItem>>([]);

    useEffect(() => {
        const fetchStats = async () => {
            const options = new ODataOptions().addSkipTop(0, 50);
            const result = await get(`/v1.0/Payroll/stats${options.toString()}`);
            if (response.ok) {
                setStats(result);
            }
        };
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedOffice) {
            setCheckDetail([]);
            return;
        }
        const fetchCheckDetail = async () => {
            const params = new URLSearchParams({
                periodNo: String(selectedOffice.periodNo ?? ''),
                type: selectedOffice.type ?? '',
                office: selectedOffice.office ?? '',
            });
            const result = await get(`/v1.0/Payroll/stats/bycheck?${params}`);
            if (response.ok) {
                setCheckDetail(result?.items ?? []);
            }
        };
        fetchCheckDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOffice]);

    const renderPeriod = () => (
        <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontWeight: 'bold' }}>Period No.</label>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={thStyle}>Period No.</th>
                        <th style={thStyle}>Checks</th>
                        <th style={thStyle}>Check Amount</th>
                        <th style={thStyle}>Deposits</th>
                        <th style={thStyle}>Deposit Amount</th>
                        <th style={thStyle}>Payments</th>
                        <th style={thStyle}>Payment Amt.</th>
                    </tr>
                </thead>
                <tbody>
                    {stats.byPeriod.map((row, index) => (
                        <tr key={index}>
                            <td style={tdStyle}>{row.periodNo}</td>
                            <td style={tdStyle}>{row.checks}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.checkAmount)}</td>
                            <td style={tdStyle}>{row.deposits}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.depositAmount)}</td>
                            <td style={tdStyle}>{row.payments}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.paymentAmount)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderType = () => (
        <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontWeight: 'bold' }}>Emp./Con. Type</label>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={thStyle}>Period No.</th>
                        <th style={thStyle}>Type</th>
                        <th style={thStyle}>Checks</th>
                        <th style={thStyle}>Check Amount</th>
                        <th style={thStyle}>Deposits</th>
                        <th style={thStyle}>Deposit Amount</th>
                        <th style={thStyle}>Payments</th>
                        <th style={thStyle}>Payment Amt.</th>
                    </tr>
                </thead>
                <tbody>
                    {stats.byType.map((row, index) => (
                        <tr key={index}>
                            <td style={tdStyle}>{row.periodNo}</td>
                            <td style={tdStyle}>{row.type}</td>
                            <td style={tdStyle}>{row.checks}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.checkAmount)}</td>
                            <td style={tdStyle}>{row.deposits}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.depositAmount)}</td>
                            <td style={tdStyle}>{row.payments}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.paymentAmount)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderOffice = () => (
        <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontWeight: 'bold' }}>Office (click a row for Check No. detail)</label>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={thStyle}>Period No.</th>
                        <th style={thStyle}>Office</th>
                        <th style={thStyle}>Checks</th>
                        <th style={thStyle}>Check Amount</th>
                        <th style={thStyle}>Deposits</th>
                        <th style={thStyle}>Deposit Amount</th>
                        <th style={thStyle}>Payments</th>
                        <th style={thStyle}>Services</th>
                        <th style={thStyle}>Expenses</th>
                        <th style={thStyle}>Payment Amt.</th>
                    </tr>
                </thead>
                <tbody>
                    {stats.byOffice.map((row, index) => (
                        <tr
                            key={index}
                            onClick={() => setSelectedOffice(row)}
                            style={{
                                cursor: 'pointer',
                                backgroundColor: selectedOffice === row ? '#cfe8ff' : undefined,
                            }}
                        >
                            <td style={tdStyle}>{row.periodNo}</td>
                            <td style={tdStyle}>{row.office}</td>
                            <td style={tdStyle}>{row.checks}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.checkAmount)}</td>
                            <td style={tdStyle}>{row.deposits}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.depositAmount)}</td>
                            <td style={tdStyle}>{row.payments}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.totService)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.totExpense)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.paymentAmount)}</td>
                        </tr>
                    ))}
                    <tr style={totalRowStyle}>
                        <td style={tdStyle} colSpan={7}>Total</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{money(sum(stats.byOffice.map((row) => row.totService)))}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{money(sum(stats.byOffice.map((row) => row.totExpense)))}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{money(sum(stats.byOffice.map((row) => row.paymentAmount)))}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );

    const renderClient = () => (
        <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontWeight: 'bold' }}>Client No.</label>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={thStyle}>Client No.</th>
                        <th style={thStyle}>Checks</th>
                        <th style={thStyle}>Check Total</th>
                        <th style={thStyle}>Deposits</th>
                        <th style={thStyle}>Deposit Total</th>
                        <th style={thStyle}>Payments</th>
                        <th style={thStyle}>Services</th>
                        <th style={thStyle}>Expenses</th>
                        <th style={thStyle}>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {stats.byClient.map((row, index) => (
                        <tr key={index}>
                            <td style={tdStyle}>{row.clNo}</td>
                            <td style={tdStyle}>{row.checks}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.checkTotal)}</td>
                            <td style={tdStyle}>{row.deps}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.depTotal)}</td>
                            <td style={tdStyle}>{row.payments}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.services)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.expenses)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.total)}</td>
                        </tr>
                    ))}
                    <tr style={totalRowStyle}>
                        <td style={tdStyle} colSpan={6}>Total</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{money(sum(stats.byClient.map((row) => row.services)))}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{money(sum(stats.byClient.map((row) => row.expenses)))}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{money(sum(stats.byClient.map((row) => row.total)))}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );

    const renderCheck = () => (
        <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontWeight: 'bold' }}>Check No.</label>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={thStyle}>Check No.</th>
                        <th style={thStyle}>Check Date</th>
                        <th style={thStyle}>Via</th>
                        <th style={thStyle}>DLC No.</th>
                        <th style={thStyle}>Paid To.</th>
                        <th style={thStyle}>Services</th>
                        <th style={thStyle}>Expenses</th>
                        <th style={thStyle}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {checkDetail.length === 0 && (
                        <tr>
                            <td style={tdStyle} colSpan={8}>
                                {selectedOffice ? 'No checks found for this Office row.' : 'Click a row in the Office section above to see its Check No. detail.'}
                            </td>
                        </tr>
                    )}
                    {checkDetail.map((row, index) => (
                        <tr key={index}>
                            <td style={tdStyle}>{row.checkNo}</td>
                            <td style={tdStyle}>{formatDate(row.checkDate)}</td>
                            <td style={tdStyle}>{disbursement(row.checkType)}</td>
                            <td style={tdStyle}>{row.tsdNo}</td>
                            <td style={tdStyle}>{row.paidTo}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.totService)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.totExpense)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(row.amount)}</td>
                        </tr>
                    ))}
                    {checkDetail.length > 0 && (
                        <tr style={totalRowStyle}>
                            <td style={tdStyle} colSpan={5}>Total</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(sum(checkDetail.map((row) => row.totService)))}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(sum(checkDetail.map((row) => row.totExpense)))}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{money(sum(checkDetail.map((row) => row.amount)))}</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    return (
        <Dialog title="Check Stats" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '75%' }}>
            <div style={{ maxHeight: '30rem', overflowY: 'scroll' }}>
                {renderPeriod()}
                {renderType()}
                {renderOffice()}
                {renderClient()}
                {renderCheck()}
            </div>
            <div className="form-row" style={{ marginTop: '1rem', justifyContent: 'center' }}>
                <Button className="blue-hover-button" style={{ width: '20%' }} text="Back" onClick={() => props.hideDialog()} />
            </div>
        </Dialog>
    );
};

export default CheckRegisterStatsDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/checkRegister/CheckRegisterStatsDialog.tsx